import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { Link } from 'components/link/link';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type {
  InitialTableState,
  TableState,
} from 'components/data-table/types';
import { PriceDecksFilters } from './price-decks-filters';
import { createPriceDeckSearchAtom, type PriceDeckParams } from './atoms';
import { STRIP_MODE_MOST_RECENT, type PriceDeckResult } from './types';
import {
  DEFAULT_PARAMS,
  METHOD_TYPE_LABELS,
  STATUS_LABELS,
  STRIP_DATE_MODE_LABELS,
  SORT_FIELD_MAP,
  columnFiltersToParams,
} from './utils';
import styles from './price-decks-content.module.scss';

// ─────────────────────────────────────
// Column Definitions
// ─────────────────────────────────────

type PriceDeckRow = PriceDeckResult & Record<string, unknown>;

const columns: DataTableColumnDef<PriceDeckRow>[] = [
  {
    accessorKey: 'name',
    header: 'Deck Name',
    size: 280,
    cell: ({ row }) => <Link to={row.original.href}>{row.original.name}</Link>,
  },
  {
    accessorKey: 'methodType',
    header: 'Method',
    size: 150,
    meta: { type: 'enum' },
    cell: ({ getValue }) => {
      const value = getValue() as string;
      return value
        ? (METHOD_TYPE_LABELS[value as keyof typeof METHOD_TYPE_LABELS] ??
            value)
        : '--';
    },
  },
  {
    accessorKey: 'oilPriceDisplay',
    header: 'Front Month Oil',
    size: 150,
    meta: { type: 'currency', format: { unit: '/BBL' }, align: 'left' },
  },
  {
    accessorKey: 'gasPriceDisplay',
    header: 'Front Month Gas',
    size: 150,
    meta: { type: 'currency', format: { unit: '/MMBTU' }, align: 'left' },
  },
  {
    accessorKey: 'stripPriceDate',
    header: 'Strip Price Date',
    size: 150,
    cell: ({ getValue, row }) => {
      const date = getValue() as string | null;
      const mode = row.original.stripDateMode;
      if (!date && mode === STRIP_MODE_MOST_RECENT)
        return STRIP_DATE_MODE_LABELS[STRIP_MODE_MOST_RECENT];
      if (!date) return '--';
      return date;
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 110,
    meta: { type: 'enum' },
    cell: ({ getValue }) => {
      const value = getValue() as string;
      return value
        ? (STATUS_LABELS[value as keyof typeof STATUS_LABELS] ?? value)
        : '--';
    },
  },
  {
    accessorKey: 'createdBy',
    header: 'Created By',
    size: 160,
    meta: { type: 'enum' },
  },
  {
    accessorKey: 'createdAt',
    header: 'Created At',
    size: 130,
    meta: { type: 'date' },
  },
  {
    accessorKey: 'updatedAt',
    header: 'Updated At',
    size: 130,
    meta: { type: 'date' },
  },
];

/** Initial table state: pinned columns, default sort, and page size. */
export const initialTableState: InitialTableState = {
  columnPinning: {
    left: ['name'],
  },
  sortColumn: 'createdAt',
  sortDirection: 'desc',
  pageSize: 100,
};

// ─────────────────────────────────────
// Content Component
// ─────────────────────────────────────

const PriceDecksContent: FC = () => {
  const { searchAtom } = useMemo(() => createPriceDeckSearchAtom(), []);
  const [searchResult, setSearchPayload] = useAtom(searchAtom);

  const [params, setParams] = useState<PriceDeckParams>(DEFAULT_PARAMS);

  // Params -> atom sync: triggers API fetch when params change.
  useEffect(() => {
    setSearchPayload(() => params);
  }, [params, setSearchPayload]);

  /**
   * Single source of truth for table-driven param updates. Wired to every
   * server-event callback so any change re-syncs the search params atom.
   * @param {TableState} dataParams - Full table state snapshot from the DataTable.
   */
  const loadData = useCallback(
    ({ page, pageSize, sorting, globalFilter, columnFilters }: TableState) => {
      const sort = sorting[0];
      setParams({
        ...DEFAULT_PARAMS,
        page,
        pageSize,
        sortAttribute: sort
          ? (SORT_FIELD_MAP[sort.id] ?? sort.id)
          : 'created_at',
        sortDirection: sort?.desc === false ? 'ASC' : 'DESC',
        text: globalFilter || undefined,
        ...columnFiltersToParams(columnFilters),
      });
    },
    []
  );

  return (
    <div className={styles.tableWrapper}>
      <DataTable<PriceDeckRow>
        data={(searchResult.data?.results ?? []) as PriceDeckRow[]}
        columns={columns as DataTableColumnDef<PriceDeckRow>[]}
        getRowId={(row) => String(row.id)}
        initialTableState={initialTableState}
        persistenceKey="price-decks-list-table"
        config={{ columnFilters: false, columnReordering: false }}
        isLoading={searchResult.loading}
        error={searchResult.error ?? null}
        scroll={{
          mode: 'paginated',
          serverDriven: true,
          totalCount: searchResult.data?.totalCount ?? 0,
        }}
        onPaginationChange={loadData}
        onSortChange={loadData}
        onFilterChange={loadData}
        onGlobalSearchChange={loadData}
        renderFilterPanel={() => (
          <PriceDecksFilters facets={searchResult.data?.facets} />
        )}
      />
    </div>
  );
};

export default PriceDecksContent;
